import React, { useState, useEffect, useMemo } from "react";
import { Select } from "components/Select/Select";
import { InputCardTitle } from "components/InputCards/InputCardTitle";
import { TooltipIcon } from "./TooltipIcon";
import { convertRalToRgb } from "./ralToRgb";

export const SelectColorCard = ({
  label,
  options,
  value,
  disabled,
  handleChange,
  partKey,
  optionKey,
  description,
  handlePartClick,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [firstTwo, setFirstTwo] = useState(null);
  const [thirdDigit, setThirdDigit] = useState(null);
  const [fourthDigit, setFourthDigit] = useState(null);

  const extractCode = (val) => {
    const match = val?.match(/RAL (\d{4})/);
    return match ? match[1] : null;
  };

  const groupedOptions = useMemo(() => {
    const map = {};
    options.forEach((opt) => {
      const code = extractCode(opt.value);
      if (!code || code.length !== 4) {
        return;
      }

      const f2 = code.slice(0, 2);
      const d3 = code[2];
      const d4 = code[3];

      if (!map[f2]) {
        map[f2] = {};
      }
      if (!map[f2][d3]) {
        map[f2][d3] = {};
      }
      map[f2][d3][d4] = opt;
    });
    return map;
  }, [options]);

  useEffect(() => {
    const code = extractCode(value?.value);
    if (!code || code.length !== 4) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFirstTwo(code.slice(0, 2));
    setThirdDigit(code[2]);
    setFourthDigit(code[3]);
  }, [value]);

  const firstTwoDigitOptions = useMemo(
    () =>
      Object.keys(groupedOptions)
        .sort()
        .map((f2) => ({ label: f2, value: f2 })),
    [groupedOptions],
  );

  const thirdDigitOptions = useMemo(() => {
    if (!firstTwo || !groupedOptions[firstTwo]) {
      return [];
    }
    return Object.keys(groupedOptions[firstTwo])
      .sort()
      .map((d3) => ({ label: d3, value: d3 }));
  }, [firstTwo, groupedOptions]);

  const fourthDigitOptions = useMemo(() => {
    if (!firstTwo || !thirdDigit || !groupedOptions[firstTwo]?.[thirdDigit]) {
      return [];
    }
    return Object.keys(groupedOptions[firstTwo][thirdDigit])
      .sort()
      .map((d4) => ({ label: d4, value: d4 }));
  }, [firstTwo, thirdDigit, groupedOptions]);

  const handleSelectChange = (level, selectedOption) => {
    setIsLoading(true);
    const val = selectedOption?.value;

    if (level === "firstTwo") {
      const newF2 = val;
      const thirdDigits = Object.keys(groupedOptions[newF2]);
      const validD3 = thirdDigits.includes(thirdDigit)
        ? thirdDigit
        : thirdDigits[0];
      const fourthDigits = Object.keys(groupedOptions[newF2]?.[validD3]);
      const validD4 = fourthDigits.includes(fourthDigit)
        ? fourthDigit
        : fourthDigits[0];

      setFirstTwo(newF2);
      setThirdDigit(validD3);
      setFourthDigit(validD4);
      handleFinalSelect(newF2, validD3, validD4);
    }

    if (level === "thirdDigit") {
      const newD3 = val;
      const fourthDigits = Object.keys(groupedOptions[firstTwo]?.[newD3]);
      const validD4 = fourthDigits.includes(fourthDigit)
        ? fourthDigit
        : fourthDigits[0];

      setThirdDigit(newD3);
      setFourthDigit(validD4);
      handleFinalSelect(firstTwo, newD3, validD4);
    }

    if (level === "fourthDigit") {
      setFourthDigit(val);
      handleFinalSelect(firstTwo, thirdDigit, val);
    }
  };

  const handleFinalSelect = (f2, d3, d4) => {
    const selected = groupedOptions[f2]?.[d3]?.[d4];
    if (selected) {
      handleChange(selected);
    }
  };

  const selectedColor = convertRalToRgb(value?.value);

  return (
    <>
      <TooltipIcon description={description} />
      <InputCardTitle
        title={label}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
      <div className="d-flex gap-4">
        <div style={{ minWidth: "170px" }}>
          <Select
            name={`config-${partKey}-${optionKey}-firstTwo`}
            options={firstTwoDigitOptions}
            value={
              firstTwoDigitOptions.find((opt) => opt.value === firstTwo) || null
            }
            disabled={disabled}
            onChange={(opt) => handleSelectChange("firstTwo", opt)}
            testId={`select-${partKey}-${optionKey}-firstTwo`}
            onMenuOpen={handlePartClick}
            menuShouldScrollIntoView={false}
          />
        </div>
        <div style={{ minWidth: "80px" }}>
          <Select
            name={`config-${partKey}-${optionKey}-thirdDigit`}
            options={thirdDigitOptions}
            value={
              thirdDigitOptions.find((opt) => opt.value === thirdDigit) || null
            }
            disabled={disabled}
            onChange={(opt) => handleSelectChange("thirdDigit", opt)}
            testId={`select-${partKey}-${optionKey}-thirdDigit`}
            onMenuOpen={handlePartClick}
            menuShouldScrollIntoView={false}
          />
        </div>
        <div style={{ minWidth: "80px" }}>
          <Select
            name={`config-${partKey}-${optionKey}-fourthDigit`}
            options={fourthDigitOptions}
            value={
              fourthDigitOptions.find((opt) => opt.value === fourthDigit) ||
              null
            }
            disabled={disabled}
            onChange={(opt) => handleSelectChange("fourthDigit", opt)}
            testId={`select-${partKey}-${optionKey}-fourthDigit`}
            onMenuOpen={handlePartClick}
            menuShouldScrollIntoView={false}
          />
        </div>
      </div>
      <div className="py-5">
        {value?.label} - {value?.description}
      </div>
      <div
        className="py-5"
        style={{ height: "100px", backgroundColor: selectedColor || "#f0f0f0" }}
      ></div>
    </>
  );
};
